'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useSyncRef } from './use-sync-ref';
const SWIPE_DISMISS_THRESHOLD_PX = 80;
const SWIPE_DISMISS_TRANSITION_MS = 200;

/** Props to spread onto a grab handle element to enable swipe-to-dismiss. */
export interface SwipeHandleProps {
  readonly onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

/** Options for the useSwipeToDismiss hook. */
export interface UseSwipeToDismissOptions {
  /** The visual surface to translate during a downward drag gesture. */
  readonly targetRef: React.RefObject<HTMLElement | null>;
  /** Called after the 200ms slide-out transition completes on an above-threshold downward drag. */
  readonly onDismiss: () => void;
}

/**
 * Shared swipe-to-dismiss algorithm, extracted from MobileFullScreenOverlay for reuse.
 * Temporarily owns the target element's inline transform, animation, and transition during a
 * drag; clears all three on snap-back so class-driven animations remain intact for subsequent
 * Radix/keyboard/backdrop dismissal paths.
 */
export function useSwipeToDismiss(t0) {
  const $ = _c(5);
  const {
    targetRef,
    onDismiss
  } = t0;
  const swipeStartYRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const snapTimerRef = useRef(null);
  const onDismissRef = useSyncRef(onDismiss);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (dismissTimerRef.current != null) {
        clearTimeout(dismissTimerRef.current);
      }
      if (snapTimerRef.current != null) {
        clearTimeout(snapTimerRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== onDismissRef || $[3] !== targetRef) {
    t3 = {
      onPointerDown(event) {
        if (activePointerIdRef.current != null && activePointerIdRef.current !== event.pointerId) {
          return;
        }
        if (dismissTimerRef.current != null) {
          clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = null;
        }
        if (snapTimerRef.current != null) {
          clearTimeout(snapTimerRef.current);
          snapTimerRef.current = null;
        }
        activePointerIdRef.current = event.pointerId;
        swipeStartYRef.current = event.clientY;
        const target = targetRef.current;
        if (target != null) {
          target.style.transform = "";
          target.style.animation = "none";
          target.style.transition = "none";
        }
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove(event_0) {
        if (activePointerIdRef.current !== event_0.pointerId) {
          return;
        }
        const startY = swipeStartYRef.current;
        const target_0 = targetRef.current;
        if (startY == null || target_0 == null) {
          return;
        }
        const dy = event_0.clientY - startY;
        target_0.style.transform = dy > 0 ? `translateY(${dy}px)` : "";
      },
      onPointerUp(event_1) {
        if (activePointerIdRef.current !== event_1.pointerId) {
          return;
        }
        const startY_0 = swipeStartYRef.current;
        const target_1 = targetRef.current;
        swipeStartYRef.current = null;
        activePointerIdRef.current = null;
        if (startY_0 == null || target_1 == null) {
          return;
        }
        const dy_0 = event_1.clientY - startY_0;
        target_1.style.transition = `transform ${SWIPE_DISMISS_TRANSITION_MS}ms ease-out`;
        if (dy_0 > SWIPE_DISMISS_THRESHOLD_PX) {
          target_1.style.transform = "translateY(100vh)";
          dismissTimerRef.current = setTimeout(() => {
            dismissTimerRef.current = null;
            onDismissRef.current();
          }, SWIPE_DISMISS_TRANSITION_MS);
        } else {
          target_1.style.transform = "";
          snapTimerRef.current = setTimeout(() => {
            snapTimerRef.current = null;
            const t = targetRef.current;
            if (t != null) {
              t.style.transform = "";
              t.style.animation = "";
              t.style.transition = "";
            }
          }, SWIPE_DISMISS_TRANSITION_MS);
        }
      },
      onPointerCancel(event_2) {
        if (activePointerIdRef.current !== event_2.pointerId) {
          return;
        }
        activePointerIdRef.current = null;
        swipeStartYRef.current = null;
        const target_2 = targetRef.current;
        if (target_2 != null) {
          target_2.style.transform = "";
          target_2.style.animation = "";
          target_2.style.transition = "";
        }
      }
    };
    $[2] = onDismissRef;
    $[3] = targetRef;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}